import type { NearestStation } from '@/wabunka-src/features/Experience/composables/query/internal/locationDetailsResponse';

const EARTH_RADIUS_KM = 6371;

/**
 * 2点間の大円距離（km）を Haversine 公式で返す。徒歩距離が取得できない駅の
 * フォールバック表示（直線距離）に用いる。
 */
export const haversineKm = (aLat: number, aLng: number, bLat: number, bLng: number): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/** 徒歩時間ラベル（<60分は "N min"、以上は "H hr" / "H hr M min"）。 */
export const formatWalkTime = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

/** メートルを "0.4 km" 形式の文字列にする。 */
export const formatKm = (meters: number): string => `${(meters / 1000).toFixed(1)} km`;

/**
 * 最寄り駅の距離ラベルを組み立てる。
 * - 徒歩時間あり: "5 min walk · 0.4 km"（徒歩距離が無ければ "5 min walk"）
 * - 徒歩時間なし: 体験座標からの直線距離 "0.4 km"（フォールバック）
 */
export const formatNearestStationLabel = (station: NearestStation, origin: { latitude: number; longitude: number }): string => {
  if (station.walkMinutes != null) {
    const walk = `${formatWalkTime(station.walkMinutes)} walk`;
    return station.walkMeters != null ? `${walk} · ${formatKm(station.walkMeters)}` : walk;
  }
  const km = haversineKm(origin.latitude, origin.longitude, station.latitude, station.longitude);
  return `${km.toFixed(1)} km`;
};
